import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { roleHome, CITIES, SERVICE_TYPES, TOW_TYPES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Wrench, Car, Building2, Truck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { key: "customer", label: "عميل", icon: Car },
  { key: "workshop", label: "ورشة", icon: Building2 },
  { key: "tow_provider", label: "سطحة", icon: Truck },
];

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState(params.get("role") || "customer");
  const [cities, setCities] = useState(CITIES);
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", city: "الرياض",
    specialization: "", tow_type: "عادية", services: [],
    bio: "", address: "", google_rating: "", google_reviews: "", google_maps_url: "",
  });

  useEffect(() => {
    api.get("/cities")
      .then(({ data }) => { if (data && data.length) setCities(data.map((c) => c.name)); })
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let user;
      if (isLogin) {
        user = await login(form.email, form.password);
      } else {
        const payload = {
          ...form, role,
          google_rating: form.google_rating ? Number(form.google_rating) : null,
          google_reviews: form.google_reviews ? Number(form.google_reviews) : null,
        };
        user = await register(payload);
      }
      if (!isLogin && (user.role === "workshop" || user.role === "tow_provider")) {
        toast.success("تم إنشاء الحساب! حسابك قيد المراجعة من الإدارة");
      } else {
        toast.success(isLogin ? "تم تسجيل الدخول" : "تم إنشاء الحساب بنجاح");
      }
      const safeFrom = isLogin && typeof from === "string" && from.startsWith("/") && !from.startsWith("//") ? from : null;
      navigate(safeFrom || roleHome(user.role));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "تعذّر إتمام العملية");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand side */}
      <div className="hidden lg:flex relative bg-gradient-to-br from-primary via-slate-900 to-slate-950 text-white p-12 flex-col justify-between overflow-hidden">
        <div className="hero-grid absolute inset-0 opacity-20" />
        <Link to="/" className="relative flex items-center gap-3 w-fit">
          <img src="/logo.webp" alt="شعار إسناد كار" width="44" height="44" loading="lazy" className="w-11 h-11 rounded-xl object-cover shadow-lg ring-1 ring-accent/40" data-testid="app-logo" />
          <span className="font-heading font-extrabold text-2xl">اسناد كار</span>
        </Link>
        <div className="relative">
          <h2 className="font-heading text-4xl font-extrabold leading-[1.4]">
            كل ما تحتاجه لصيانة سيارتك <br /> في مكان واحد
          </h2>
          <p className="text-white/80 mt-4 text-lg leading-relaxed max-w-md">
            طلبات صيانة، عروض أسعار من الورش، وسطحات مع تتبع مباشر. تجربة سلسة من الطلب حتى الاستلام.
          </p>
        </div>
        <div className="relative text-white/60 text-sm">منصة سعودية لخدمات السيارات</div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <Card className="w-full max-w-md p-6 sm:p-8 border-border">
          <div className="mb-6">
            <h1 className="font-heading text-2xl font-extrabold">{isLogin ? "تسجيل الدخول" : "إنشاء حساب جديد"}</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              {isLogin ? "أدخل بياناتك للمتابعة" : "اختر نوع الحساب وأكمل بياناتك"}
            </p>
          </div>

          {!isLogin && (
            <div className="grid grid-cols-3 gap-2 mb-5">
              {ROLES.map((r) => (
                <button key={r.key} type="button" onClick={() => setRole(r.key)} data-testid={`role-tab-${r.key}`}
                  className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 transition-colors ${role === r.key ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground"}`}>
                  <r.icon className="w-5 h-5" />
                  <span className="text-xs font-semibold">{r.label}</span>
                </button>
              ))}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label className="mb-1.5 block">{role === "workshop" ? "اسم الورشة" : role === "tow_provider" ? "اسم مقدم الخدمة" : "الاسم الكامل"}</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="input-name" placeholder="أدخل الاسم" />
              </div>
            )}
            <div>
              <Label className="mb-1.5 block">البريد الإلكتروني</Label>
              <Input type="email" dir="ltr" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="input-email" placeholder="you@example.com" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="block">كلمة المرور</Label>
                {isLogin && <Link to="/forgot-password" className="text-xs text-primary font-semibold hover:underline" data-testid="forgot-password-link">نسيت كلمة المرور؟</Link>}
              </div>
              <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required data-testid="input-password" placeholder="••••••••" />
            </div>

            {!isLogin && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="mb-1.5 block">رقم الجوال</Label>
                    <Input dir="ltr" value={form.phone} onChange={(e) => set("phone", e.target.value)} required data-testid="input-phone" placeholder="05xxxxxxxx" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">المدينة</Label>
                    <Input value={form.city} onChange={(e) => set("city", e.target.value)} required
                      list="cities-list" data-testid="input-city" placeholder="اكتب اسم المدينة" />
                    <datalist id="cities-list">
                      {cities.map((c) => <option key={c} value={c} />)}
                    </datalist>
                  </div>
                </div>
                {role === "workshop" && (
                  <div>
                    <Label className="mb-1.5 block">التخصص</Label>
                    <Input value={form.specialization} onChange={(e) => set("specialization", e.target.value)} data-testid="input-spec" placeholder="مثال: سيارات يابانية / جميع الأنواع" />
                  </div>
                )}
                {role === "tow_provider" && (
                  <div>
                    <Label className="mb-1.5 block">نوع السطحة</Label>
                    <Select value={form.tow_type} onValueChange={(v) => set("tow_type", v)} dir="rtl">
                      <SelectTrigger data-testid="select-towtype"><SelectValue /></SelectTrigger>
                      <SelectContent>{TOW_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {(role === "workshop" || role === "tow_provider") && (
                  <>
                    <div>
                      <Label className="mb-1.5 block">نبذة تعريفية</Label>
                      <Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} data-testid="input-bio" rows={2}
                        placeholder={role === "workshop" ? "نبذة مختصرة عن الورشة وخبراتها" : "نبذة مختصرة عن الخدمة"} />
                    </div>
                    {role === "workshop" && (
                      <div>
                        <Label className="mb-1.5 block">عنوان/موقع الورشة</Label>
                        <Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="input-address" placeholder="الحي، الشارع، المدينة" />
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="mb-1.5 block">تقييم قوقل ماب</Label>
                        <Input type="number" step="0.1" min="0" max="5" value={form.google_rating} onChange={(e) => set("google_rating", e.target.value)} data-testid="input-grating" placeholder="مثال: 4.6" />
                      </div>
                      <div>
                        <Label className="mb-1.5 block">عدد تقييمات قوقل</Label>
                        <Input type="number" min="0" value={form.google_reviews} onChange={(e) => set("google_reviews", e.target.value)} data-testid="input-greviews" placeholder="مثال: 120" />
                      </div>
                    </div>
                    <div>
                      <Label className="mb-1.5 block">رابط قوقل ماب (اختياري)</Label>
                      <Input dir="ltr" value={form.google_maps_url} onChange={(e) => set("google_maps_url", e.target.value)} data-testid="input-gmaps" placeholder="https://maps.google.com/..." />
                    </div>
                    <p className="text-xs text-muted-foreground -mt-1">سيظهر رقمك التعريفي تلقائياً بعد اعتماد الحساب من الإدارة (مثال: ورشة معتمدة رقم 1001).</p>
                  </>
                )}
              </>
            )}

            <Button type="submit" className="w-full rounded-xl h-11 text-base" disabled={loading} data-testid="submit-auth">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : isLogin ? "دخول" : "إنشاء الحساب"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            {isLogin ? (
              <>ليس لديك حساب؟ <Link to="/register" className="text-primary font-semibold" data-testid="link-register">إنشاء حساب</Link></>
            ) : (
              <>لديك حساب؟ <Link to="/login" className="text-primary font-semibold" data-testid="link-login">تسجيل الدخول</Link></>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
